import axios, { AxiosError } from 'axios';
import { useCallback } from 'react';
import { CompleteOperationType, StartOperationType } from '../model/alerts.model';
import { DocumentsService } from '../service/documents.service';
import { Utilities } from '../utilities/utilities';

interface UseFileUploaderResult {
  uploadSelectedFiles: (filesParam?: File[] | FileList) => Promise<void>;
}

const useFileUploader = (
  clientId: string,
  startOperation: StartOperationType,
  completeOperation: CompleteOperationType,
): UseFileUploaderResult => {
  async function uploadDocument(clientId: string, file: File) {
    try {
      const params = new URLSearchParams({
        fileName: Utilities.removeFileExtension(file.name),
        fileSize: file.size.toString(),
        contentType: file.type,
      });

      const presignedResponse = await DocumentsService.getPresigned(clientId, params);
      const { url, fields } = presignedResponse;
      const formData = new FormData();

      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });

      formData.append('file', file);
      await (
        await axios.post(url, formData)
      ).data;

      return { ok: true };
    } catch (error) {
      return { ok: false, error };
    }
  }

  const uploadSelectedFiles = useCallback(
    async (filesParam?: File[] | FileList) => {
      const filesToUpload: File[] = filesParam ? Array.from(filesParam) : [];

      if (filesToUpload.length === 0) {
        return;
      }

      startOperation('upload', 'Uploading...');

      try {
        const uploadPromises = filesToUpload.map((file) => uploadDocument(clientId, file));
        const results = await Promise.all(uploadPromises);
        const failedUploads = results.filter((r) => !r.ok);

        if (failedUploads.length > 0) {
          const failedCount = failedUploads.length;
          const totalCount = filesToUpload.length;
          const failedDetails = failedUploads
            .map((r) => {
              return r.error instanceof AxiosError
                ? r.error.response?.data.error
                : String(r.error ?? 'Unknown error');
            })
            .join('; ');

          const message = `${failedCount}/${totalCount} file(s) failed to upload${
            failedDetails ? `: ${failedDetails}` : '.'
          }`;

          completeOperation('upload', `${message}`, true);
        } else {
          completeOperation('upload', 'Upload successful');
        }
      } catch (err: unknown) {
        if (typeof err === 'string') {
          completeOperation('upload', `${err}`, true);
        } else {
          completeOperation('upload', 'Unknown error occurred', true);
        }
      }
    },
    [clientId, completeOperation, startOperation],
  );

  return {
    uploadSelectedFiles,
  };
};

export default useFileUploader;
